import { useEffect, useState } from 'react';

import { FunderOnboardingStatuses, ProductTypeCodes } from 'entities/progress';
import { BusinessProductXrefSchema } from 'services/businessProducts';
import { hasPermission } from 'pages/dashboard/selectors';
import { useAppDispatch, useAppSelector } from 'hooks';
import { Permissions } from 'entities/dashboard';
import {
  getBusinessPerson,
  getFunderOnboardingStatus,
} from 'pages/Profile/selectors';

import CapitalProductGetStartedModal from './CapitalProductGetStartedModal';
import { updateBusinessPersonXref, updateBusinessProduct } from '../thunks';
import { fetchFinancialData } from '../../Financial/thunks';
import ProgressProduct from './ProgressProduct';
import {
  getActiveProductsTypeCodes,
  getBusinessProducts,
  getIsCapitalProductActive,
} from '../selectors';

type ProgressProductsProps = {
  filter?: ProductTypeCodes[];
  inline?: boolean;
  isProgressSection?: boolean;
};

const ProgressProducts: React.FC<ProgressProductsProps> = ({
  filter = [],
  inline = false,
  isProgressSection = false,
}) => {
  const dispatch = useAppDispatch();

  const products = useAppSelector(getBusinessProducts);
  const isCapitalProductActive = useAppSelector(getIsCapitalProductActive);
  const activeProductsTypeCodes = useAppSelector(getActiveProductsTypeCodes);
  const businessPerson = useAppSelector(getBusinessPerson);
  const funderOnboardingStatus = useAppSelector(getFunderOnboardingStatus);
  const productPermissions = useAppSelector((state) =>
    hasPermission(state, Permissions.products),
  );
  const implementationsPermissions = useAppSelector((state) =>
    hasPermission(state, Permissions.implementations),
  );
  const compliancePermissions = useAppSelector((state) =>
    hasPermission(state, Permissions.compliance),
  );

  const [showGetStartedModal, setShowGetStartedModal] =
    useState<boolean>(false);

  useEffect(() => {
    if (
      isCapitalProductActive &&
      businessPerson.workingCapitalOrchestrationFirstAlertedOn === '' &&
      funderOnboardingStatus !== FunderOnboardingStatuses.approved &&
      funderOnboardingStatus !== FunderOnboardingStatuses.declined
    ) {
      setShowGetStartedModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Disable products if
  // 1. Capital product is active and user doesn't have permissions to edit implementations and compliance.
  // 2. AP/AR product is disabled but product requires it to be active (e.g. Services).
  const isProductDisabled = (product: BusinessProductXrefSchema) => {
    if (product.isActive) {
      return false;
    }

    if (
      isCapitalProductActive &&
      !implementationsPermissions.edit &&
      !compliancePermissions.edit &&
      !activeProductsTypeCodes.includes(ProductTypeCodes.payments)
    ) {
      return true;
    }

    if (
      product.productsTypeCode !== ProductTypeCodes.payments &&
      !activeProductsTypeCodes.includes(ProductTypeCodes.payments) &&
      !product.isActive
    ) {
      return true;
    }

    return false;
  };

  const handleClickProduct = async (product: BusinessProductXrefSchema) => {
    await dispatch(
      updateBusinessProduct({
        businessesProductsXrefId: product.businessesProductsXrefId,
        isActive: !product.isActive,
        productsTypeCode: product.productsTypeCode,
      }),
    );

    if (product.productsTypeCode === ProductTypeCodes.services) {
      dispatch(fetchFinancialData());
    }
  };

  const handleModalClose = () => {
    dispatch(updateBusinessPersonXref());
    setShowGetStartedModal(false);
  };

  return (
    <>
      {showGetStartedModal && isProgressSection && (
        <CapitalProductGetStartedModal onClose={handleModalClose} />
      )}

      {products
        .filter(
          (prod: BusinessProductXrefSchema) =>
            !filter.length || filter.includes(prod.productsTypeCode),
        )
        .sort(
          (a, b) =>
            Object.values(ProductTypeCodes).indexOf(a.productsTypeCode) -
            Object.values(ProductTypeCodes).indexOf(b.productsTypeCode),
        )
        .map((product: BusinessProductXrefSchema) => (
          <ProgressProduct
            key={product.businessesProductsXrefId}
            item={product}
            onClick={handleClickProduct}
            inline={inline}
            permission={productPermissions.edit}
            disabled={isProductDisabled(product)}
            hasComplianceOrImplementationsPermissions={
              compliancePermissions.edit || implementationsPermissions.edit
            }
          />
        ))}
    </>
  );
};

export default ProgressProducts;
